import { ReactElement } from 'react';

import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { atomWithReset, loadable, RESET } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';

import { DTOProfileCard } from 'src/apis/dto/DTOProfileCard';
import { ProfileQueryKeys } from 'src/hooks/useProfileQuery';
import {
  acceptCmpConsentsAtom,
  getCmpConsentsAtom,
  handleLoginResultAtom,
  socialTokenAtom,
  tryLoginTypeAtom,
  userJoinDateAtom,
} from 'src/stores/auth/atoms';
import { statusAtom } from 'src/stores/match/atoms';
import {
  isRemoveOldDeviceUserDetailsAtom,
  phoneNumberLoginEmailAtom,
  smsPhoneNumberAtom,
} from 'src/stores/phoneNumberLogin/atoms';
import { updateMainProfileImageAPI } from 'src/stores/profile/apis';
import { setFeatureSettingsAtom } from 'src/stores/user/atom';
import { SetFeatureSettings, SetFeatureSettingsParam } from 'src/stores/user/types';
import { AzarUser } from 'src/types/AzarUser';
import { ConsentsMapRequest } from 'src/types/consent';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { STATUS } from 'src/types/Match';
import {
  GENDER,
  LoginType,
  NonBinaryChildGender,
  RegisterParamsBase,
  SignUpSettingsResponse,
} from 'src/types/register';
import { RPCError } from 'src/utils/api';
import { pushAppsflyerEvent } from 'src/utils/appsflyer/pba';
import { uploadContentManager } from 'src/utils/content-manager';
import getDeviceId from 'src/utils/device/id';
import { pushEvent } from 'src/utils/event-gateway';
import { optimisticUpdaterFactory } from 'src/utils/query';

import {
  getSignUpSettingsAPI,
  registerOAuthAPI,
  RegisterOAuthParams,
  saveEmailAPI,
  updateUserEthnicityAPI,
} from './apis';

export const enum LoginModalType {
  LOGIN_ENTRY, // 최초 모달
  SOCIAL_LOGIN, // SNS 로그인(email 인증이 필요한 경우)
  SOCIAL_REGISTER, // SNS 회원가입
  PHONE_NUMBER_LOGIN, // 전화번호 로그인 및 회원가입. + SNS 로그인 시 2차 인증이 필요한 경우. 추후 분리하여 개선.
}
export const loginModalTypeAtom = atom<LoginModalType>(LoginModalType.LOGIN_ENTRY);
export const isRegisterLoadingAtom = atom(false);
export const isRegisterCompleteAtom = atomWithReset(false);

export const birthAtom = atomWithReset<Date | null>(null);
export const genderAtom = atomWithReset<GENDER | undefined>(undefined);
export const childGenderAtom = atomWithReset<NonBinaryChildGender | undefined>(undefined);
/**
 * 민족성 선택을 건너뛴 경우 서버에 전달하는 값
 * (하나의 민족성 옵션처럼 처리)
 */
export const ETHNICITY_SKIP_OPTION = 'SKIP' as const;

export type EthnicitySelection =
  | {
      /** SKIP 옵션은 옵션 리스트에서 선택하거나 스킵 버튼을 통해 선택 가능 */
      key: typeof ETHNICITY_SKIP_OPTION;
      source: 'option' | 'skipButton';
    }
  | {
      /** SKIP 외의 옵션은 옵션 리스트에서만 선택 가능 */
      key: string;
      source: 'option';
    };

export const ethnicityAtom = atomWithReset<EthnicitySelection | null>(null);

export const primaryLanguageCodeAtom = atomWithReset<string | null>(null);

type RegisterProfileNicknameState =
  | {
      /**
       * 닉네임 입력 관련 어떠한 입력 등 어떠한 동작도 하지 않은 유저 (미대상 유저 포함)
       */
      status: 'pending';
    }
  | {
      /**
       * 닉네임 입력 후, 제출하기 전 상태
       */
      status: 'draft';
      nickname: string;
    }
  | {
      /**
       * 닉네임 입력 후, 제출 확정된 상태
       */
      status: 'selected';
      nickname: string;
    }
  | {
      /**
       * 닉네임 입력이 요구되었으나 이를 스킵한 경우
       */
      status: 'skipped';
    };

export const nicknameAtom = atomWithReset<RegisterProfileNicknameState>({
  status: 'pending',
});

type RegisterProfileImageState =
  | {
      /**
       * 프로필 입력 관련 어떠한 스킵, 제출 등 어떠한 동작도 하지 않은 유저 (미대상 유저 포함)
       */
      status: 'pending';
    }
  | {
      /**
       * 프로필 이미지 입력 후, 제출하기 전 상태
       */
      status: 'draft';
      file: File;
    }
  | {
      /**
       * 프로필 이미지 입력 후, 제출 확정된 상태
       */
      status: 'selected';
      file: File;
    }
  | {
      /**
       * 프로필 이미지 입력이 요구되었으나 이를 스킵한 경우
       */
      status: 'skipped';
    };

export const profileImageStateAtom = atomWithReset<RegisterProfileImageState>({
  status: 'pending',
});

export const socialRegisterHistoryAtom = atomWithReset<JSX.Element[]>([]);

export const pushSocialRegisterHistoryAtom = atom(null, (get, set, nextStep: ReactElement) => {
  set(socialRegisterHistoryAtom, (history) => [...history, nextStep]);
});

export const popSocialRegisterHistoryAtom = atom(null, (get, set) => {
  set(socialRegisterHistoryAtom, (history) => history.slice(0, -1));
});

export const featureSettingParamsFromOptionalConsentsAtom = atomWithReset<
  SetFeatureSettings[] | null
>(null);

export const requiredConsentsMapForRegisterAtom = atomWithReset<
  ConsentsMapRequest['consents'] | null
>(null);

export const legalNoticeMapForRegisterAtom = atomWithReset<
  RegisterParamsBase['legalNoticesConfirmed'] | null
>(null);

export const purgeBeforeSignUpAtom = atomWithReset<RegisterParamsBase['purgeBeforeSignUp']>(false);

export const resetRegisterAtom = atom(null, (get, set) => {
  set(featureSettingParamsFromOptionalConsentsAtom, RESET);
  set(requiredConsentsMapForRegisterAtom, RESET);
  set(statusAtom, STATUS.INITIAL);
  set(socialRegisterHistoryAtom, RESET);
  set(loginModalTypeAtom, LoginModalType.LOGIN_ENTRY);
  set(isRemoveOldDeviceUserDetailsAtom, false);
  set(purgeBeforeSignUpAtom, false);
  set(smsPhoneNumberAtom, RESET);
  set(isRegisterCompleteAtom, RESET);
  set(birthAtom, RESET);
  set(genderAtom, RESET);
  set(childGenderAtom, RESET);
  set(nicknameAtom, RESET);
  set(ethnicityAtom, RESET);
  set(primaryLanguageCodeAtom, RESET);
  set(profileImageStateAtom, RESET);
});

export const registerAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      phoneVerification?: {
        verifyCode: string;
        formattedPhoneNumber: string;
      };
      featureSettings?: SetFeatureSettingsParam['featureSettings'];
      cmpConsentsAccepted: boolean;
    }
  ): Promise<[AzarUser, null] | [null, RPCError<Record<string, unknown>> | Error]> => {
    set(isRegisterLoadingAtom, true);
    const loginType = get(tryLoginTypeAtom);
    const socialToken = get(socialTokenAtom);
    const birth = get(birthAtom);
    const gender = get(genderAtom);
    const nickname = get(nicknameAtom);

    if (loginType === undefined || !socialToken || !birth || !gender) {
      return [null, new Error('ValidationError')];
    }

    const profileImageState = get(profileImageStateAtom);
    const primaryLanguageCode = get(primaryLanguageCodeAtom);
    const requestBody = {
      socialToken,
      birthYear: birth.getFullYear(),
      birthMonth: birth.getMonth() + 1,
      birthDay: birth.getDate(),
      ...params,
      gender,
      childGender: get(childGenderAtom)?.key,
      nickname: nickname.status === 'selected' ? nickname.nickname : undefined,
      nicknameSkipped: nickname.status === 'skipped',
      consents: get(requiredConsentsMapForRegisterAtom) ?? {},
      legalNoticesConfirmed: get(legalNoticeMapForRegisterAtom) ?? {},
      purgeBeforeSignUp: get(purgeBeforeSignUpAtom),
      profileImageChosen: profileImageState.status === 'selected',
      profileImageSkipped: profileImageState.status === 'skipped',
      ...(primaryLanguageCode
        ? {
            conversationLanguage: {
              primaryLanguageCode,
            },
          }
        : {}),
    } satisfies RegisterOAuthParams;
    const response = await registerOAuthAPI(loginType, requestBody);
    try {
      const {
        data: { result: userData, error },
      } = response;
      if (userData && !error) {
        set(handleLoginResultAtom, response);

        if (userData.accessToken) {
          if (loginType === LoginType.SMS) {
            saveEmailAPI({ email: get(phoneNumberLoginEmailAtom).email });
          }

          const selectedEthnicity = get(ethnicityAtom);
          if (selectedEthnicity) {
            updateUserEthnicityAPI({ ethnicityKeys: [selectedEthnicity.key] });
          }

          // 프로필 이미지 업로드
          if (profileImageState.status === 'selected') {
            uploadContentManager(profileImageState.file)
              .then(async (fileInfo) => {
                const { data } = await updateMainProfileImageAPI(fileInfo);
                get(queryClientAtom).setQueryData(
                  ProfileQueryKeys.getProfileCard('MY_PROFILE'),
                  optimisticUpdaterFactory<DTOProfileCard>((prev) => {
                    prev.profileImageInfos = data;
                  })
                );
              })
              .catch((uploadError) => {
                Sentry.captureException(uploadError);
              });
          }

          const featureSettings = [
            ...(params.featureSettings ?? []),
            ...(get(featureSettingParamsFromOptionalConsentsAtom) ?? []),
          ];

          Promise.allSettled([
            params.cmpConsentsAccepted ? set(acceptCmpConsentsAtom) : set(getCmpConsentsAtom),
            featureSettings.length > 0 && set(setFeatureSettingsAtom, featureSettings),
          ]).then((results) => {
            results.forEach((result) => {
              if (result.status === 'rejected') {
                Sentry.captureException(result.reason);
              }
            });
          });

          set(isRegisterCompleteAtom, true);
        }
        pushEvent({
          eventType: EVENT_TYPE.SIGN_UP,
          eventName: EVENT_NAME.SIGNUP_COMPLETE,
          eventParams: {
            userId: userData.userId,
            loginType: get(tryLoginTypeAtom),
          },
          options: { user_id: userData.userId },
        });
        pushAppsflyerEvent('signup');

        set(userJoinDateAtom, new Date());
        set(resetRegisterAtom);
        return [userData, null];
      } else if (error) {
        if (
          // TODO: Exception 정의 개선
          ![
            'NotVerifiedException', // 2차 인증이 필요한 경우(한국)
            'VerificationFailedException', // 2차 인증 실패
            'InsufficientInformationException', // 추가정보 입력 필요(현재는 nickname)
          ].includes(error.message)
        ) {
          Sentry.captureEvent({
            message: 'Unexpected Register Error Message',
            extra: {
              error: JSON.stringify(error),
              loginType,
              requestBody: JSON.stringify({
                ...requestBody,
                socialToken: '',
              }).replace('socialToken', ''),
              dd: getDeviceId(),
            },
          });
        }

        return [null, error];
      } else {
        return [null, new Error('Register Failed')];
      }
    } catch (e) {
      Sentry.captureException(e);
      return [null, e instanceof Error ? e : new Error('Register Failed')];
    } finally {
      set(isRegisterLoadingAtom, false);
    }
  }
);

export const FALLBACK_SIGNUP_SETTINGS: SignUpSettingsResponse = {
  genderSettings: {
    nonBinaryChildGenders: [],
    nonBinaryEnabled: false,
    hideGenderOptionEnabled: false,
    hideGenderDefaultValue: false,
  },
  birthdaySettings: {
    blockAlertAge: 1,
    uiType: 'BIRTHDAY_WEB_B',
  },
  nicknameSettings: {
    showNicknameSkipButton: false,
  },
  profileImageSettings: {
    skipEnabled: true,
  },
};

export const fetchSignUpSettingsAtom = atom<
  Promise<SignUpSettingsResponse> | SignUpSettingsResponse
>(async () => {
  const { data } = await getSignUpSettingsAPI();
  return data;
});

export const signUpSettingsLoadableAtom = loadable(fetchSignUpSettingsAtom);
